import { useMemo } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useResultsBehindProfile } from './useResultsBehindProfile';
import { useAnalysisResultsQuery } from '../api/useAnalysisResultsQuery';
import { useAnalysisVersionQuery } from '../api/useAnalysisVersionQuery';
import { selectLoadedVersionId } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { ResultsStaleness, UseAnalysisDefinitionTabResult } from '../types';

export const useAnalysisDefinitionTab = (): UseAnalysisDefinitionTabResult => {
  const { projectId, analysisProfileId, analysisVersionId } = useAnalysisRouteParams();

  const versionQuery = useAnalysisVersionQuery({
    projectId,
    analysisProfileId,
    analysisVersionId,
  });
  const loadedVersionId = useAnalysisDefinitionStore(selectLoadedVersionId);

  const resultsData = useAnalysisResultsQuery({ projectId, analysisProfileId }).data?.data;
  // The cached results still carry the previous run's stale flag until the
  // post-run refetch lands, so the banner is withheld rather than shown stale.
  const isResultsBehindProfile = useResultsBehindProfile();
  const resultsStaleness = useMemo<ResultsStaleness | null>(
    () =>
      resultsData?.stale && !isResultsBehindProfile
        ? { reasons: Array.from(new Set(resultsData.staleReasons)) }
        : null,
    [resultsData, isResultsBehindProfile]
  );

  // "Loading" = the definition store hasn't reflected the URL's version yet:
  // route params not ready, query in flight, or the query succeeded but the
  // hydration effect hasn't run (including right after a save navigates to a
  // new version id). ``isRefetching`` covers a background refetch of an
  // already-loaded version (e.g. the RA edited the analysis and the
  // ``RESOURCE_UPDATED`` invalidation is re-fetching it), so the sections show
  // their loading state instead of stale data.
  const isLoading =
    versionQuery.isLoading ||
    versionQuery.isRefetching ||
    !analysisVersionId ||
    loadedVersionId !== analysisVersionId;

  return { isLoading, resultsStaleness };
};
